/** PROTOTYPE board: rows are WBS LEAF nodes, showing EVERY activity in the
 *  scope leaves — takted bars wear their takt pill (work-code color), untakted
 *  bars are grey pills carrying the activity name, and anchor endpoints
 *  (activities tied to outside/finished work) are highlighted with an explicit
 *  ⚓ IN / ⚓ OUT tag. Takt is ignored for layout — plan against the WBS,
 *  takt it at the end.
 *
 *  Interactions (completed bars are display-only):
 *    drag a bar        — move it in time (a selected bar drags the whole selection)
 *    drag its right lip— resize (duration)
 *    click             — popover: rename / days / delete
 *    shift-click       — toggle into the selection
 *    drag empty space  — marquee-select
 *    palette chip drop — stage a NEW activity in that leaf at that day
 */
import React from "react";
import type { DepGraphPredType, ExistingWorkCode, ScopeMember } from "../../../models";
import type { DepCandidate } from "../depCore/DepChipTypeahead/DepChipTypeahead";
import { computeLeafRows, laneAtY, type BarSpan, type LeafRow } from "./boardLayout";
import { useBoardDrag } from "./hooks";
import { TRAIN_LEAF_KEY } from "../PullPlanGrid/train/trainReconcile";
import BoardBar from "./BoardBar/BoardBar";
import ZoneHeader from "./ZoneHeader/ZoneHeader";
import "./WbsBoardView.css";

export type AnchorDir = "in" | "out" | "both";

export type { BarSpan } from "./boardLayout";

interface Props {
  members: ScopeMember[];
  calendar: string[];
  calendarIndexFor: (iso: string | null) => number;
  workCodeColors: Record<string, string>;
  anchorDir: Map<number, AnchorDir>;
  dayPx: number;
  labelWidth: number;
  laneHeight: number;
  /** Moved/resized bars: activity id -> its current (overridden) span. */
  spanOverrides?: Map<number, BarSpan>;
  /** True while a palette work-code chip is dragged — arms the rows. */
  dropArmed?: boolean;
  /** A work-code chip dropped on a leaf row at a day column. */
  onDropCode?: (leaf: { key: string; path: string[] }, dayIndex: number) => void;
  /** A fresh train is armed for placement — the board takes a grab cursor +
   *  level highlight, marquee is suppressed, and clicking empty space on a leaf
   *  drops the run there. */
  placeArmed?: boolean;
  /** Click on empty space of a leaf while armed: drop the train run at this day +
   *  lane (the run's anchor). */
  onPlaceClick?: (
    leaf: { key: string; path: string[] },
    dayIndex: number,
    laneIndex: number,
  ) => void;
  /** Unstage a draft-added activity (the ✕ on a NEW bar / popover Delete). */
  onRemoveNew?: (activityId: number) => void;
  /** Bar(s) dragged to a new day (and optionally a new row / lane). */
  onMoveBars?: (
    moves: Array<{ activityId: number; startIndex: number; leafKey?: string; lane?: number }>,
  ) => void;
  /** A multi-selection was dropped — the host asks move vs duplicate. */
  onBulkDrop?: (intent: {
    ids: number[];
    dxDays: number;
    leafKey: string | null;
    lane: number | null;
  }) => void;
  /** Pinned lane per bar (manual vertical placement within a level). */
  laneOverrides?: Map<number, number>;
  /** FS-chain lane grouping: activity_id → the activity it follows F→S, so a run
   *  stays on one lane (SS/FF/SF/untied work greedy-fills around it). */
  fsLinks?: Map<number, number>;
  /** Staged re-codes: canonical work code -> displayed code. */
  codeDisplay?: Record<string, string>;
  /** Bar resized to a new duration (workdays). */
  onResizeBar?: (activityId: number, durationDays: number) => void;
  /** Delete a committed activity (routes the host's outside-ties guard). */
  onDeleteBar?: (activityId: number) => void;
  /** Rename an activity (staged; new bars patch their entry). */
  onRenameBar?: (activityId: number, name: string) => void;
  /** Change an activity's work code (staged taktification / entry patch). */
  onRecodeBar?: (
    activityId: number,
    draft: { code: string; name: string; color: string },
  ) => void;
  scheduleId?: number;
  paletteCodes?: ExistingWorkCode[];
  selectedIds?: Set<number>;
  onSelectionChange?: (next: Set<number>) => void;
  /** Freeze the given bars at their current lane before a drag commits, so the
   *  board behaves like a canvas (a move never re-packs untouched bars). */
  onPinLanes?: (pairs: Array<[number, number]>) => void;
  readOnly?: boolean;
  /** Render completed work as bars (the read-only completed-plan view); the
   *  live board omits it. Default false. */
  showCompleted?: boolean;
  /** Display order of leaf keys (drag ⠿ to reorder); unlisted keys follow. */
  rowOrder?: string[];
  onRowOrderChange?: (keys: string[]) => void;
  /** Bar hover for the bottom inspector (null on leave). */
  onHoverBar?: (m: ScopeMember | null) => void;
  /** Baseline latest early finish (day index) — drawn as a dashed line. */
  baselineEndIndex?: number | null;
  /** Highlight every bar wearing this work code; others dim. */
  highlightCode?: string | null;
  /** User-added empty rows (below the WBS leaves). */
  extraRows?: Array<{ key: string; label: string }>;
  /** ALL scope leaves — pre-seeds a row per leaf so wiped/empty leaves still
   *  render their structure. */
  seedLeaves?: Array<{ key: string; label: string; path: string[] }>;
  /** The pinned virtual TRAIN lane (display-only cars), rendered above the WBS
   *  leaves when a train exists. */
  trainRow?: LeafRow | null;
  /** Committed activity ids mapped to the train — highlighted as "this train". */
  trainMappedRefs?: Set<number>;
  /** Live ghost-preview: activity ids on OTHER levels where the current
   *  selection's run repeats — ringed so you see where a train would land. */
  previewRefs?: Set<number>;
  /** Per-leaf pull-target chip ("«27» 6/8") for the zone header. */
  targetByLeaf?: Map<string, { code: string; date: string | null }>;
  /** Controlled lane selection (rail pill + full-row glow; tap-append target). */
  selectedLane?: { leafKey: string; lane: number } | null;
  onSelectLane?: (sel: { leafKey: string; lane: number } | null) => void;
  /** Unified board drill-in: the leaf (level) whose row is selected — clicking a
   *  zone header selects it (and the gantt below focuses it). null ⇒ none. */
  selectedLeafKey?: string | null;
  onSelectLeaf?: (leafKey: string | null) => void;
  /** Bar-popover pred plumbing: candidates from the dep graph, the driving
   *  pred per bar, and the set-pred intent (graph instance ids). */
  depCandidates?: DepCandidate[];
  drivingPredByBarId?: Map<
    number,
    {
      code: string;
      color: string;
      name: string;
      rel: string;
      predId: string;
      type: DepGraphPredType;
      lagDays: number;
    }
  >;
  onSetPredEdge?: (
    barId: number,
    currentPredId: string | null,
    newPredId: string | null,
    type: DepGraphPredType,
    lagDays: number,
  ) => void;
  /** Remove the bar's current driving predecessor tie. */
  onRemovePredEdge?: (barId: number, predId: string) => void;
  /** Train-car bar pred row: candidates are the OTHER train elements, and the
   *  pred edits the train's internal ties (same shapes as the section pred row). */
  trainDepCandidates?: DepCandidate[];
  trainDrivingPredByBar?: Map<
    number,
    {
      code: string;
      color: string;
      name: string;
      rel: string;
      predId: string;
      type: DepGraphPredType;
      lagDays: number;
    }
  >;
  onSetTrainPred?: (
    barId: number,
    currentPredId: string | null,
    newPredId: string | null,
    type: DepGraphPredType,
    lagDays: number,
  ) => void;
  onRemoveTrainPred?: (barId: number, predId: string) => void;
  /** The bar's dep-graph id (committed id or draft_id) — self-filter key. */
  barGraphId?: (activityId: number) => string;
  onRenameRow?: (key: string, label: string) => void;
  onDeleteRow?: (key: string) => void;
  /** Extra empty lanes per leaf (vertical room inside a level). */
  extraLanes?: Map<string, number>;
  onChangeLanes?: (leafKey: string, delta: number) => void;
  /** Effective Takt level per leaf (override ?? derived). null ⇒ "set level". */
  levelByLeaf?: Map<string, string | null>;
  /** Each leaf's real Takt section/zone (modal of its members) — the read-only
   *  "UNIT · BLKR" context chip on the leaf header. */
  taktContextByLeaf?: Map<string, { takt_a: string | null; takt_b: string | null }>;
  /** Optional richer hover tooltip for a bar (e.g. train elements get
   *  duration + predecessors + successors). Falls back to code · name. */
  barTip?: (m: ScopeMember) => string | undefined;
  /** Count of mis-coded members per leaf (drives the ⚠ pill variant). */
  miscodeCountByLeaf?: Map<string, number>;
  /** Leaves flagged "not Takt work" — the pill is replaced with a ⚙ chip. */
  notaktLeaves?: Set<string>;
  /** Section Takt_C menu for the level pill's chooser. */
  taktCMenu?: string[];
  /** Set a leaf's level (stages takt_c on its members). */
  onSetLeafLevel?: (leafKey: string, level: string) => void;
  /** Toggle a leaf's "not Takt work" flag. */
  onToggleNotakt?: (leafKey: string) => void;
}

const EMPTY_SET = new Set<number>();

const WbsBoardView: React.FC<Props> = ({
  members,
  calendar,
  calendarIndexFor,
  workCodeColors,
  anchorDir,
  dayPx,
  labelWidth,
  laneHeight,
  spanOverrides,
  dropArmed = false,
  onDropCode,
  placeArmed = false,
  onPlaceClick,
  onRemoveNew,
  onMoveBars,
  onBulkDrop,
  onResizeBar,
  onDeleteBar,
  onRenameBar,
  onRecodeBar,
  scheduleId,
  paletteCodes,
  selectedIds = EMPTY_SET,
  onSelectionChange,
  onPinLanes,
  readOnly = false,
  showCompleted,
  rowOrder,
  onRowOrderChange,
  onHoverBar,
  baselineEndIndex,
  highlightCode,
  extraRows,
  seedLeaves,
  trainRow,
  trainMappedRefs,
  previewRefs,
  targetByLeaf,
  selectedLane = null,
  onSelectLane,
  selectedLeafKey = null,
  onSelectLeaf,
  depCandidates,
  drivingPredByBarId,
  onSetPredEdge,
  onRemovePredEdge,
  trainDepCandidates,
  trainDrivingPredByBar,
  onSetTrainPred,
  onRemoveTrainPred,
  barGraphId,
  onRenameRow,
  onDeleteRow,
  extraLanes,
  onChangeLanes,
  laneOverrides,
  fsLinks,
  codeDisplay,
  levelByLeaf,
  taktContextByLeaf,
  barTip,
  miscodeCountByLeaf,
  notaktLeaves,
  taktCMenu,
  onSetLeafLevel,
  onToggleNotakt,
}) => {
  const [renamingRow, setRenamingRow] = React.useState<string | null>(null);
  // Bar labels read by the WORK CODE's name (like the strip chips), falling
  // back to the activity name for codes nothing has named.
  const codeNameByKey = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const c of paletteCodes ?? []) {
      if (c.prefix && c.prefix !== c.code) m.set(c.code, c.prefix);
    }
    return m;
  }, [paletteCodes]);
  const extraKeys = React.useMemo(
    () => new Set((extraRows ?? []).map((r) => r.key)),
    [extraRows],
  );
  const [dragLeaf, setDragLeaf] = React.useState<string | null>(null);
  const [dropLeaf, setDropLeaf] = React.useState<{ key: string; pos: "above" | "below" } | null>(null);

  const leaves = React.useMemo<LeafRow[]>(() => {
    const taktByLeaf = taktContextByLeaf
      ? new Map(
          Array.from(taktContextByLeaf, ([k, v]) => [
            k,
            { takt_a: v.takt_a, takt_b: v.takt_b, takt_c: levelByLeaf?.get(k) ?? null },
          ]),
        )
      : undefined;
    const rows = computeLeafRows({
      members,
      calendarIndexFor,
      spanOverrides,
      laneOverrides,
      rowOrder,
      extraRows,
      extraLanes,
      seedLeaves,
      taktByLeaf,
      fsLinks,
      showCompleted,
    });
    // The train lane pins to the TOP, ahead of every WBS leaf (bypasses the
    // path/rowOrder sort).
    return trainRow ? [trainRow, ...rows] : rows;
  }, [members, calendarIndexFor, spanOverrides, rowOrder, extraRows, extraLanes, laneOverrides, seedLeaves, trainRow, taktContextByLeaf, levelByLeaf, fsLinks, showCompleted]);

  const spanByIdRef = React.useRef(new Map<number, BarSpan>());
  spanByIdRef.current = React.useMemo(() => {
    const m = new Map<number, BarSpan>();
    for (const leaf of leaves) {
      for (const b of leaf.bars) {
        m.set(b.member.activity_id, {
          startIndex: b.startIndex,
          durationDays: b.durationDays,
        });
      }
    }
    return m;
  }, [leaves]);

  const editable = (m: ScopeMember) =>
    !readOnly && m.status !== "TK_Complete";

  const dropRowOn = (targetKey: string) => {
    if (!dragLeaf || !onRowOrderChange || dragLeaf === targetKey) {
      setDragLeaf(null);
      setDropLeaf(null);
      return;
    }
    const keys = leaves.map((l) => l.key).filter((k) => k !== dragLeaf);
    const idx = keys.indexOf(targetKey);
    const at = dropLeaf?.pos === "below" ? idx + 1 : idx;
    keys.splice(at < 0 ? keys.length : at, 0, dragLeaf);
    onRowOrderChange(keys);
    setDragLeaf(null);
    setDropLeaf(null);
  };

  const {
    rootRef,
    drag,
    marquee,
    editingId,
    setEditingId,
    editAnchorRef,
    startBarDrag,
    startResize,
    startMarquee,
    dragMoveOffset,
    dragResizeExtra,
  } = useBoardDrag({
    leaves,
    spanByIdRef,
    calendar,
    dayPx,
    laneHeight,
    labelWidth,
    readOnly,
    selectedIds,
    editable,
    onMoveBars,
    onBulkDrop,
    onResizeBar,
    onSelectionChange,
    onPinLanes,
  });

  const gridWidth = calendar.length * dayPx;

  return (
    <div
      className={
        "wbs-board" +
        (highlightCode ? " wbs-board--has-hl" : "") +
        (placeArmed ? " wbs-board--place" : "") +
        (drag ? (drag.kind === "resize" ? " wbs-board--resizing" : " wbs-board--dragging") : "")
      }
      ref={rootRef}
      onMouseDown={placeArmed ? undefined : startMarquee}
      onMouseLeave={() => onHoverBar?.(null)}
    >
      {baselineEndIndex != null &&
        baselineEndIndex >= 0 &&
        baselineEndIndex < calendar.length && (
          <div
            className="wbs-board-baseline-end"
            style={{ left: labelWidth + (baselineEndIndex + 1) * dayPx }}
            title="Baseline latest early finish — where this chunk ended before the pull"
          />
        )}
      {marquee && (
        <div
          className="wbs-board-marquee"
          style={{
            left: marquee.left,
            top: marquee.top,
            width: marquee.width,
            height: marquee.height,
          }}
        />
      )}
      {drag?.kind === "bulk" && (
        <div
          className="wbs-board-bulk-outline"
          style={{
            left: drag.box.left + drag.dxPx,
            top: drag.box.top + drag.dyPx,
            width: drag.box.width,
            height: drag.box.height,
          }}
        >
          <span className="wbs-board-bulk-outline-count">
            {drag.ids.size} activities
          </span>
        </div>
      )}
      {leaves.map((leaf) => {
        const rowHeight = leaf.lanes * laneHeight + 8;
        const target = targetByLeaf?.get(leaf.key);
        const laneSelected =
          selectedLane && selectedLane.leafKey === leaf.key ? selectedLane.lane : null;
        const tgtDate = (iso: string | null) => {
          if (!iso) return "";
          const [, mo, da] = iso.split("-");
          return `${Number(mo)}/${Number(da)}`;
        };
        return (
          <div
            className={
              "wbs-board-zone" +
              (leaf.key === TRAIN_LEAF_KEY ? " wbs-board-zone--train" : "") +
              (leaf.key === selectedLeafKey ? " wbs-board-zone--selected" : "")
            }
            key={leaf.key}
            data-wbs-leaf={leaf.key}
          >
            <ZoneHeader
              leaf={leaf}
              readOnly={readOnly}
              selectedLeafKey={selectedLeafKey}
              onSelectLeaf={onSelectLeaf}
              dragLeaf={dragLeaf}
              setDragLeaf={setDragLeaf}
              dropLeaf={dropLeaf}
              setDropLeaf={setDropLeaf}
              dropRowOn={dropRowOn}
              onRowOrderChange={onRowOrderChange}
              renamingRow={renamingRow}
              setRenamingRow={setRenamingRow}
              onRenameRow={onRenameRow}
              onDeleteRow={onDeleteRow}
              isExtra={extraKeys.has(leaf.key)}
              target={target}
              tgtDate={tgtDate}
              taktContextByLeaf={taktContextByLeaf}
              levelByLeaf={levelByLeaf}
              miscodeCountByLeaf={miscodeCountByLeaf}
              notaktLeaves={notaktLeaves}
              taktCMenu={taktCMenu}
              onSetLeafLevel={onSetLeafLevel}
              onToggleNotakt={onToggleNotakt}
              onSelectionChange={onSelectionChange}
              editable={editable}
            />
            <div className="wbs-board-row" style={{ height: rowHeight }}>
              <div className="wbs-board-lanerail" style={{ width: labelWidth }}>
                {Array.from({ length: leaf.lanes }).map((_, li) => {
                  const isSel = laneSelected === li;
                  return (
                    <div
                      key={li}
                      className="wbs-board-lane-slot"
                      style={{ top: 4 + li * laneHeight, height: laneHeight - 4 }}
                    >
                      <button
                        type="button"
                        className={
                          "wbs-board-lane" + (isSel ? " wbs-board-lane--selected" : "")
                        }
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectLane?.(
                            selectedLane &&
                              selectedLane.leafKey === leaf.key &&
                              selectedLane.lane === li
                              ? null
                              : { leafKey: leaf.key, lane: li },
                          );
                        }}
                      >
                        <span className="wbs-board-lane-dot" aria-hidden="true" />
                        Lane {li + 1}
                        {isSel && <span className="wbs-board-lane-tag">SELECTED</span>}
                      </button>
                    </div>
                  );
                })}
              </div>
            <div
              className={
                "wbs-board-cells" +
                (dropArmed ? " wbs-board-cells--armed" : "") +
                (placeArmed && leaf.key !== TRAIN_LEAF_KEY ? " wbs-board-cells--place" : "")
              }
              style={{
                width: gridWidth,
                backgroundImage:
                  "linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px), " +
                  "linear-gradient(to bottom, rgba(255, 255, 255, 0.06) 1px, transparent 1px)",
                backgroundSize: `${dayPx}px 100%, 100% ${laneHeight}px`,
                backgroundPosition: `0 0, 0 2px`,
              }}
              onClick={(e) => {
                // Place mode: a click on empty space of a real leaf drops the run
                // here. Clicks on a bar fall through to the bar (reposition/edit).
                if (
                  !placeArmed ||
                  !onPlaceClick ||
                  leaf.key === TRAIN_LEAF_KEY ||
                  (e.target as HTMLElement).closest("[data-wbs-bar]")
                ) {
                  return;
                }
                const rect = e.currentTarget.getBoundingClientRect();
                const day = Math.max(
                  0,
                  Math.min(
                    calendar.length - 1,
                    Math.floor((e.clientX - rect.left) / dayPx),
                  ),
                );
                const lane = laneAtY(e.clientY, rect.top, laneHeight);
                onPlaceClick({ key: leaf.key, path: leaf.path }, day, lane);
              }}
              onDragOver={(e) => {
                if (dropArmed) e.preventDefault();
              }}
              onDrop={(e) => {
                if (!dropArmed || !onDropCode) return;
                e.preventDefault();
                const rect = e.currentTarget.getBoundingClientRect();
                const day = Math.max(
                  0,
                  Math.min(
                    calendar.length - 1,
                    Math.floor((e.clientX - rect.left) / dayPx),
                  ),
                );
                onDropCode({ key: leaf.key, path: leaf.path }, day);
              }}
            >
              {laneSelected != null && (
                <div
                  className="wbs-board-lane-glow"
                  style={{
                    top: 4 + laneSelected * laneHeight - 2,
                    height: laneHeight,
                  }}
                />
              )}
              {drag?.kind === "move" && drag.target?.leafKey === leaf.key && (
                <div
                  className="wbs-board-drop-ghost"
                  style={{
                    left: drag.target.startIndex * dayPx,
                    width: drag.primaryDuration * dayPx,
                    top: 4 + drag.target.lane * laneHeight,
                    height: laneHeight - 4,
                  }}
                />
              )}
              {leaf.bars.map((bar) => (
                <BoardBar
                  key={bar.member.activity_id}
                  bar={bar}
                  leaf={leaf}
                  dayPx={dayPx}
                  laneHeight={laneHeight}
                  workCodeColors={workCodeColors}
                  anchorDir={anchorDir}
                  selectedIds={selectedIds}
                  editingId={editingId}
                  inDrag={drag?.kind === "move" && drag.ids.has(bar.member.activity_id)}
                  highlightCode={highlightCode}
                  trainMappedRefs={trainMappedRefs}
                  previewRefs={previewRefs}
                  codeDisplay={codeDisplay}
                  codeNameByKey={codeNameByKey}
                  editable={editable}
                  barTip={barTip}
                  dragMoveOffset={dragMoveOffset}
                  dragResizeExtra={dragResizeExtra}
                  scheduleId={scheduleId}
                  paletteCodes={paletteCodes}
                  editAnchorRef={editAnchorRef}
                  setEditingId={setEditingId}
                  onStartBarDrag={startBarDrag}
                  onStartResize={startResize}
                  onHoverBar={onHoverBar}
                  onSelectionChange={onSelectionChange}
                  onRenameBar={onRenameBar}
                  onResizeBar={onResizeBar}
                  onRecodeBar={onRecodeBar}
                  onRemoveNew={onRemoveNew}
                  onDeleteBar={onDeleteBar}
                  depCandidates={depCandidates}
                  drivingPredByBarId={drivingPredByBarId}
                  onSetPredEdge={onSetPredEdge}
                  onRemovePredEdge={onRemovePredEdge}
                  trainDepCandidates={trainDepCandidates}
                  trainDrivingPredByBar={trainDrivingPredByBar}
                  onSetTrainPred={onSetTrainPred}
                  onRemoveTrainPred={onRemoveTrainPred}
                  barGraphId={barGraphId}
                />
              ))}
            </div>
              {leaf.undated.length > 0 && (
                <span className="wbs-board-undated" title="No dates inside the visible calendar">
                  +{leaf.undated.length} undated
                </span>
              )}
            </div>
            {!readOnly && onChangeLanes && (
              <div className="wbs-board-lanefoot" style={{ width: labelWidth }}>
                {(extraLanes?.get(leaf.key) ?? 0) > 0 && (
                  <button
                    type="button"
                    className="wbs-board-zone-btn"
                    title="Remove the empty lane at the bottom of this zone"
                    onClick={() => onChangeLanes(leaf.key, -1)}
                  >
                    − Lane
                  </button>
                )}
                <button
                  type="button"
                  className="wbs-board-zone-btn wbs-board-zone-btn--add"
                  title="Add an empty lane at the bottom of this zone"
                  onClick={() => onChangeLanes(leaf.key, 1)}
                >
                  + Lane
                </button>
              </div>
            )}
          </div>
        );
      })}
      {leaves.length === 0 && (
        <div className="wbs-board-empty">No scope members loaded yet…</div>
      )}
    </div>
  );
};

export default WbsBoardView;
